import {createContext, useContext, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject} from "react";

export type TableTopBounds = {width: number; height: number};
type OverlayLayout = {
    viewport: TableTopBounds;
    panelBounds: TableTopBounds;
    toolbarRef: RefObject<HTMLElement | null>;
};
const OverlayBoundsContext = createContext<OverlayLayout | null>(null);

/** One measurement source for all overlays; coordinates are relative to the map container. */
export function TableTopOverlayBoundsProvider({children}: {children: ReactNode}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const toolbarRef = useRef<HTMLElement>(null);
    const [bounds, setBounds] = useState({viewport: {width: 0, height: 0}, panelBounds: {width: 0, height: 0}});
    useLayoutEffect(() => {
        const container = containerRef.current;
        const toolbar = toolbarRef.current;
        if (!container) return;
        const measure = () => {
            const rect = container.getBoundingClientRect();
            const height = toolbar ? Math.max(0, Math.min(rect.height, toolbar.getBoundingClientRect().top - rect.top)) : rect.height;
            setBounds(current => current.viewport.width === rect.width && current.viewport.height === rect.height
                && current.panelBounds.height === height ? current : {
                    viewport: {width: rect.width, height: rect.height}, panelBounds: {width: rect.width, height},
                });
        };
        measure();
        window.addEventListener("resize", measure);
        const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(measure);
        observer?.observe(container);
        if (toolbar) observer?.observe(toolbar);
        return () => {observer?.disconnect(); window.removeEventListener("resize", measure);};
    }, []);
    const value = useMemo(() => ({...bounds, toolbarRef}), [bounds]);
    return <div ref={containerRef} className="relative size-full min-h-0 overflow-hidden">
        <OverlayBoundsContext.Provider value={value}>{children}</OverlayBoundsContext.Provider>
    </div>;
}

/** Shared popovers also work in the dungeon editor, where the complete viewport is available. */
export function useTableTopOverlayBounds(fallbackViewport?: TableTopBounds): {
    viewport: TableTopBounds; panelBounds: TableTopBounds;
} {
    const layout = useContext(OverlayBoundsContext);
    if (layout) return {viewport: layout.viewport, panelBounds: layout.panelBounds};
    if (fallbackViewport) return {viewport: fallbackViewport, panelBounds: fallbackViewport};
    throw new Error("useTableTopOverlayBounds must be used within TableTopOverlayBoundsProvider");
}

export function useTableTopOverlayToolbarRef() {
    return useContext(OverlayBoundsContext)?.toolbarRef;
}
